import React, { useState, useEffect } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";

import PageLoader from "../Components/PageLoader";
import Overview from "../Components/Overview";
import DBConnection from "../Components/DBConnection";
import Settings from "../Components/Settings";
import DataBackup from "../Components/DataBackup";
import HistoricalData from "../Components/HistoricalData/HistoricalData";
import {
  verifyZohoAuth,
  requestZohoAuthUrl,
  revokeZohoAuth,
  createExtensionWidget,
  buildZohoAuthHeaders,
  verifyDbConnection,
} from "../utils/api";
import { detectDataCenterUrl, setOrgVariable, fetchExtensionSettings } from "../utils/zoho";
import { ORG_VARS } from "../utils/orgVariables";
import {
  BRAND_COLOR,
  BRAND_COLOR_TINT,
  DIVIDER,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from "../utils/colors";

const ZOHO = window.ZOHO;

const RELATED_LIST_WIDGET = {
  widgetName: "MySQL Sync by Easy Pluginz",
  widgetUrl: "https://widgets.v1.mysqlsync.easy-pluginz.com/relatedlist",
  widgetDescription:
    "This widget you can utilize in any Standard Module and Custom Module to check history and restore data",
  widgetType: "RELATED_LIST",
};

export default function SettingsRoute() {
  const [authLoadingZoho, setAuthLoadingZoho] = useState(false);
  const [revokeLoadingZoho, setRevokeLoadingZoho] = useState(false);
  const [zohoAuth, setZohoAuth] = useState(false);
  const [connectionUrl, setConnectionUrl] = useState(null);
  const [disabledTabs, setDisabledTabs] = useState({
    dbTab: false,
    settingsTab: false,
    backupData: false,
    historical: false,
  });

  const [snackbarMsg, setSnackbarMsg] = useState("");

  const [snackBarForError, setSnackBarForError] = useState(false);

  const [tabNumber, setTabNumber] = useState(null);
  const [zohoLoaded, setZohoLoaded] = useState(false);

  const [loading, setLoading] = useState(true);

  const [apiKey, setApiKey] = useState();

  const [orgId, setOrgId] = useState();
  const [zapiKey, setZapiKey] = useState();
  const [dataCenterUrl, setDataCenterUrl] = useState("");
  const [zuid, setZuid] = useState(null);

  const tabText = {
    minHeight: 40,
    justifyContent: "flex-start",
    textAlign: "left",
    textTransform: "none",
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_SECONDARY,
    borderRadius: "8px",
    mx: 1,
    my: 0.25,
    px: 1.5,
    py: 1,
    "&.Mui-selected": {
      color: BRAND_COLOR,
      bgcolor: BRAND_COLOR_TINT,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 19,
    },
  };

  useEffect(() => {
    async function initZoho() {
      ZOHO.embeddedApp.on("PageLoad", async function (data) {
        ZOHO.CRM.CONFIG.getOrgInfo().then(function (data) {
          console.log("Get Org Info", data);
        });

        const detectedDataCenterUrl = await detectDataCenterUrl();
        setDataCenterUrl(detectedDataCenterUrl);
        await setOrgVariable(ORG_VARS.DATA_CENTER_URL, detectedDataCenterUrl);

        setZohoLoaded(true);
      });
      ZOHO.embeddedApp.init();
    }
    initZoho();
  }, []);

  useEffect(() => {
    if (zohoLoaded) {
      // fetch previous saved data for external service and zoho crm
      fetchOrgVariablesData()
        .then(async (result) => {
          if (!result) return;

          const { apiKey, orgId, connectionUrl } = result;
          try {
            if (connectionUrl === "null") {
              setTabNumber(1);
              setDisabledTabs({
                settingsTab: true,
                backupData: true,
                historical: true,
              });
              return;
            }

            const connectionOk = await verifyDbConnection(
              { orgId, apiKey },
              connectionUrl
            );
            if (connectionOk) {
              setZohoAuth(true);
              setTabNumber(2);
            } else {
              setTabNumber(1);
            }
          } catch (error) {
            setTabNumber(1);
          }
        })
        .then(() => {
          setLoading(false);
        });
    }
  }, [zohoLoaded]);

  const dbConnectionCheck = async () => {
    try {
      const connectionOk = await verifyDbConnection({ orgId, apiKey }, connectionUrl);
      setTabNumber(connectionOk ? 2 : 1);
    } catch (error) {
      setTabNumber(1);
    }
  };

  const handleAuthenticateZoho = async () => {
    setAuthLoadingZoho(true);

    const authUrl = await requestZohoAuthUrl({
      orgId,
      apiKey,
      zapiKey,
      dataCenterUrl,
      zuid,
    });

    const authWindow = window.open(
      authUrl,
      "windowName",
      "height=640,width=1024"
    );
    if (window.focus && authWindow) {
      authWindow.focus();
    }

    const myAuthCheckInterval = setInterval(async () => {
      const authCheckStatus = await verifyZohoAuth(
        buildZohoAuthHeaders({ orgId, apiKey, dataCenterUrl })
      );

      if (authCheckStatus) {
        setZohoAuth(true);
        setAuthLoadingZoho(false);
        clearInterval(myAuthCheckInterval);
        dbConnectionCheck();
      } else if (authWindow?.closed) {
        // popup was closed / canceled without completing auth — stop waiting
        setAuthLoadingZoho(false);
        clearInterval(myAuthCheckInterval);
      }
    }, 5000);
  };

  const handleRevokeZoho = async () => {
    setRevokeLoadingZoho(true);

    const revoked = await revokeZohoAuth({ orgId, apiKey });

    if (revoked) {
      setZohoAuth(false);
    }
    setRevokeLoadingZoho(false);
  };

  const fetchOrgVariablesData = async () => {
    // Get previously saved org variables of this extension
    const settings = await fetchExtensionSettings({
      includeDbUrl: true,
      dataCenterFallback: dataCenterUrl,
    });

    setDataCenterUrl(settings.dataCenterUrl);
    setApiKey(settings.apiKey);
    setConnectionUrl(settings.dbConnectionUrl);
    setOrgId(settings.orgId);
    setZapiKey(settings.zapiKey);

    // Fetch user details
    const orgResp = await ZOHO.CRM.CONFIG.getOrgInfo();
    setZuid(orgResp?.org?.[0]?.primary_zuid);

    const authCheckStatusZoho = await verifyZohoAuth(
      buildZohoAuthHeaders({
        orgId: settings.orgId,
        apiKey: settings.apiKey,
        dataCenterUrl: settings.dataCenterUrl,
      })
    );

    if (authCheckStatusZoho) {
      // Make sure the related-list widget exists in this org
      await createExtensionWidget({
        orgId: settings.orgId,
        apiKey: settings.apiKey,
        dataCenterUrl: settings.dataCenterUrl,
        widget: RELATED_LIST_WIDGET,
      });

      return {
        apiKey: settings.apiKey,
        orgId: settings.orgId,
        connectionUrl: settings.dbConnectionUrl,
      };
    } else {
      setTabNumber(0);
      setDisabledTabs({
        dbTab: true,
        settingsTab: true,
        backupData: true,
        historical: true,
      });
    }
    setLoading(false);
    return null;
  };

  const handleTabChange = (event, newValue) => {
    setTabNumber(newValue);
  };

  if (loading) {
    return <PageLoader fullScreen message="Loading your settings..." />;
  }

  return (
    <>
      <Snackbar
        open={snackBarForError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={(event, reason) => {
          if (reason === "clickaway") {
            return;
          }

          setSnackBarForError(false);
        }}
      >
        <MuiAlert
          onClose={(event, reason) => {
            if (reason === "clickaway") {
              return;
            }

            setSnackBarForError(false);
          }}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
          {/* You might be entered a wrong API KEY. */}
        </MuiAlert>
      </Snackbar>
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          width: "100%",
          minHeight: "100vh",
        }}
      >
        {/* Left sidebar navigation */}
        <Box
          sx={{
            width: 230,
            flexShrink: 0,
            borderRight: `1px solid ${DIVIDER}`,
            bgcolor: "transparent",
            pt: 0.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Extension name (text only — no logo) */}
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: TEXT_PRIMARY,
                whiteSpace: "nowrap",
              }}
            >
              Easy MySQL Sync
            </Typography>
          </Box>
          <Tabs
            orientation="vertical"
            value={tabNumber}
            onChange={handleTabChange}
            TabIndicatorProps={{ sx: { display: "none" } }}
          >
            <Tab
              sx={tabText}
              icon={<VpnKeyRoundedIcon />}
              iconPosition="start"
              label="Authentication"
              disableFocusRipple
              disableRipple
            />
            <Tab
              sx={tabText}
              icon={<StorageRoundedIcon />}
              iconPosition="start"
              label="DB Connection"
              disableFocusRipple
              disableRipple
              disabled={disabledTabs.dbTab}
            />
            <Tab
              sx={tabText}
              icon={<SettingsRoundedIcon />}
              iconPosition="start"
              label="Settings"
              disableFocusRipple
              disableRipple
              disabled={disabledTabs.settingsTab}
            />
            <Tab
              sx={tabText}
              icon={<CloudSyncRoundedIcon />}
              iconPosition="start"
              label="Backup / Restore"
              disableFocusRipple
              disableRipple
              disabled={disabledTabs.backupData}
            />
            <Tab
              sx={tabText}
              icon={<HistoryRoundedIcon />}
              iconPosition="start"
              label="Historical Data"
              disableFocusRipple
              disableRipple
              disabled={disabledTabs.historical}
            />
          </Tabs>

        </Box>

        {/* Tab content */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {tabNumber === 0 && (
            <Overview
              handleRevokeZoho={handleRevokeZoho}
              revokeLoadingZoho={revokeLoadingZoho}
              authLoadingZoho={authLoadingZoho}
              zohoAuth={zohoAuth}
              handleAuthenticateZoho={handleAuthenticateZoho}
              orgId={orgId}
              dataCenterUrl={dataCenterUrl}
            />
          )}
          {tabNumber === 1 && (
            <DBConnection
              setTabNumber={setTabNumber}
              setDisabledTabs={setDisabledTabs}
              orgId={orgId}
              apiKey={apiKey}
              handleConnectionUrl={(url) => {
                setConnectionUrl(url);
              }}
            />
          )}
          {tabNumber === 2 && (
            <Settings
              orgId={orgId}
              apiKey={apiKey}
              connectionUrl={connectionUrl}
              dataCenterUrl={dataCenterUrl}
              zapiKey={zapiKey}
            />
          )}
          {tabNumber === 3 && (
            <DataBackup
              orgId={orgId}
              apiKey={apiKey}
              dataCenterUrl={dataCenterUrl}
              zapiKey={zapiKey}
              connectionUrl={connectionUrl}
            />
          )}
          {tabNumber === 4 && (
            <HistoricalData
              orgId={orgId}
              zohoApiKey={apiKey}
              dataCenterUrl={dataCenterUrl}
              zapiKey={zapiKey}
              connectionUrl={connectionUrl}
            />
          )}
        </Box>
      </Box>
    </>
  );
}
